import { ECountry, ELocale } from 'App/i18n/i18n.types';
import { addMonths, format, parseISO } from 'date-fns';
import { es, ptBR } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

import {
  Statement,
  StatementsByDate
} from 'domains/billing/pre-paid/wallet-statement/wallet-statement.types';

import {
  ARG_DNI_MAX_LENGTH,
  ARG_DNI_MIN_LENGTH,
  ARG_CUIT_LENGTH
} from '../constants';
import { ArgentinaProvinces, MexicoProvinces } from '../types/commons';
import { GenericShipmentRequestDto } from '../types/genericShipmentRequestDto';

const shortTrackingCode = (tracking: string) => {
  return `${tracking.slice(0, 3)}...${tracking.slice(
    tracking.length - 6,
    tracking.length
  )}`;
};

const formatDocument = (value: string) => {
  const document = value.replace(/[^A-Z0-9]/gi, '');

  if (document.length === 11) {
    return document.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/g, '$1.$2.$3-$4');
  }

  return document
    .replace(
      /^([A-Z0-9]{2})([A-Z0-9]{3})([A-Z0-9]{3})([A-Z0-9]{4})(\d{2})$/i,
      '$1.$2.$3/$4-$5'
    )
    .toUpperCase();
};

const formatZipcode = (zipcode: string) => {
  const regex = /(^[0-9]{5})-?([0-9]{3}$)/;
  if (regex.test(zipcode)) {
    return zipcode;
  }

  return '';
};

const formatZipcodeFriendly = (zipcode: string) => {
  return zipcode.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

const groupStatementsByDate = (statements: Statement[]): StatementsByDate => {
  return statements.reduce<StatementsByDate>((acc, current) => {
    const currentDate = format(parseISO(current.createdAt), 'yyyy-MM-dd');

    if (!acc[currentDate]) acc[currentDate] = [];
    acc[currentDate].push(current);

    return acc;
  }, {});
};

const formatDate = (date: Date, format: string) => {
  return formatInTimeZone(date, 'UTC', format);
};

const formatFriendlyDate = (
  date: Date,
  format: string,
  storeLanguage: string
) => {
  return formatInTimeZone(date, 'UTC', format, {
    locale: storeLanguage === 'pt-BR' ? ptBR : es
  });
};

export const formatMonthFromIsoDate = (
  isoString: string,
  storeLanguage: string,
  monthOffset = 0
) =>
  format(addMonths(parseISO(isoString), monthOffset), 'LLLL', {
    locale: storeLanguage === 'pt-BR' ? ptBR : es
  });

const friendlyAddress = (
  address: GenericShipmentRequestDto.Address,
  country?: ECountry
) => {
  if (country === ECountry.ARGENTINA) {
    const province =
      address?.province && address?.province?.length > 2
        ? address.province
        : ArgentinaProvinces.find(
            (location) => location.code === address.province
          )?.name;

    if (address.neighborhood) {
      return `${address.address}, ${address.number} ${
        address.complement ? ` - ${address.complement}` : ''
      } - ${address.neighborhood} - ${address.city} - ${province}`;
    }

    return `${address.address}, ${address.number} ${
      address.complement ? ` - ${address.complement}` : ''
    } - ${address.city} - ${province}`;
  }

  if (country === ECountry.MEXICO) {
    const province =
      address.province.length > 3
        ? address.province
        : MexicoProvinces.find((location) => location.code === address.province)
            ?.name;

    if (address.neighborhood) {
      return `${address.address}, ${address.number} ${
        address.complement ? ` - ${address.complement}` : ''
      } - ${address.neighborhood} - ${address.city} - ${province}`;
    }

    return `${address.address}, ${address.number} ${
      address.complement ? ` - ${address.complement}` : ''
    } - ${address.city} - ${province}`;
  }

  return `${address.address}, ${address.number} ${
    address.complement ? ` - ${address.complement}` : ''
  } - ${address.neighborhood} - ${address.city} - ${address.province}`;
};

const currencyConfig = (locale: string) => {
  if (locale === ELocale.ARGENTINA || locale === ELocale.MEXICO) {
    return {
      symbol: '$',
      decimal: ',',
      separator: '.'
    };
  }

  return {
    symbol: 'R$',
    decimal: ',',
    separator: '.'
  };
};

const capitalizeFirstLetter = (value: string) => {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const convertNumberStringToFloat = (value: string) => {
  return parseFloat(value.replace('.', '').replace(',', '.'));
};

export class CpfCnpjUtils {
  static isCpfValid(cpf: string): boolean {
    const cpfLength = 11;
    const weights = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    return this.isValid(cpf, cpfLength, weights);
  }

  static isCnpjValid(cnpj: string): boolean {
    const cnpjLength = 14;
    const weights = [2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5, 6];
    const cleaned = CpfCnpjUtils.removeCnpjMask(cnpj);

    if (
      cleaned.length !== cnpjLength ||
      CpfCnpjUtils.isAllTheSameDigits(cleaned)
    ) {
      return false;
    }

    const body = cleaned.substring(0, 12);
    const expectedCheckers = cleaned.substring(12);

    const check1 = this.calcCheckerChar(body, weights);
    const check2 = this.calcCheckerChar(body + check1, weights);
    const calculatedCheckers = check1 + check2;

    return calculatedCheckers === expectedCheckers;
  }

  private static isValid(
    digits: string,
    correctDigitsLength: number,
    weights: number[]
  ): boolean {
    const cleanDigits = this.getOnlyNumbers(digits);
    if (
      cleanDigits.length !== correctDigitsLength ||
      this.isAllTheSameDigits(cleanDigits)
    ) {
      return false;
    }
    const digitsWithoutChecker = cleanDigits.substring(
      0,
      correctDigitsLength - 2
    );
    const digitsChecker = cleanDigits.substring(
      correctDigitsLength - 2,
      correctDigitsLength
    );
    const calculetedChecker = this.calcChecker(digitsWithoutChecker, weights);
    return digitsChecker === calculetedChecker;
  }

  private static removeCnpjMask(value: string): string {
    return value.replace(/[^A-Z0-9]/gi, '').toUpperCase();
  }

  // Converts a character to its ordinal value per Receita Federal's alphanumeric CNPJ spec:
  // digits 0-9 → 0-9; letters A-Z → 10-35
  private static charToOrdinal(char: string): number {
    return char.toUpperCase().charCodeAt(0) - 48;
  }

  private static ordinalToChar(ordinal: number): string {
    if (ordinal <= 9) {
      return String(ordinal);
    }
    return String.fromCharCode(ordinal + 55);
  }

  private static getOnlyNumbers(digits: string): string {
    return digits.replace(/\D/g, '');
  }

  private static isAllTheSameDigits(digits: string): boolean {
    return !digits.split('').some((digit) => digit !== digits[0]);
  }

  private static weightedSum(digits: string, weights: number[]): number {
    const digitsLength = digits.length;
    return digits.split('').reduce((acc, digit, idx) => {
      return (
        acc +
        CpfCnpjUtils.charToOrdinal(digit) * weights[digitsLength - 1 - idx]
      );
    }, 0);
  }

  private static calcCheckerChar(digits: string, weights: number[]): string {
    const remainder = this.weightedSum(digits, weights) % 11;
    const checkerValue = remainder < 2 ? 0 : 11 - remainder;

    return this.ordinalToChar(checkerValue);
  }

  private static calcChecker(digits: string, weights: number[]): string {
    const digitsLength = digits.length;
    const digitsLengthWithoutChecker = weights.length - 1;

    const sum = this.weightedSum(digits, weights);
    const sumDivisionRemainder = sum % 11;
    const checker = sumDivisionRemainder < 2 ? 0 : 11 - sumDivisionRemainder;

    if (digitsLength === digitsLengthWithoutChecker) {
      return this.calcChecker(`${digits}${checker}`, weights);
    }

    return `${digits[digitsLength - 1]}${checker}`;
  }
}

const isArgentinianLegalIdentificationValid = (
  identification: string
): boolean => {
  return (
    identification.length === ARG_DNI_MAX_LENGTH ||
    identification.length === ARG_DNI_MIN_LENGTH ||
    identification.length === ARG_CUIT_LENGTH
  );
};

const formatFiscalKey = (value: string, maxLength: number) => {
  const digits = value.replace(/\D/g, '').slice(0, maxLength);
  return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
};

export {
  shortTrackingCode,
  formatDocument,
  groupStatementsByDate,
  formatZipcode,
  formatZipcodeFriendly,
  formatDate,
  formatFriendlyDate,
  friendlyAddress,
  currencyConfig,
  capitalizeFirstLetter,
  convertNumberStringToFloat,
  isArgentinianLegalIdentificationValid,
  formatFiscalKey
};
